const GROUP_SELECTOR = '[data-filter-group]'
const TARGET_SELECTOR = '[data-filter-target]'
const NAME_SELECTOR = '[data-filter-name]'
const COUNT_SELECTOR = '[data-filter-count]'

const COUNT_TEMPLATE_ATTRIBUTE = 'data-filter-count-template'
const COUNT_SHOWN_TOKEN = '[shown]'

const TARGET_MATCH_ATTRIBUTE = 'data-filter-target-match'
const TARGET_MATCH_SELECTOR = '[data-filter-target-match]'

const RESET_TARGETS = new Set(['all', 'reset'])
const TRANSITION_MS = 300

function readTokens(element) {
  const raw = (element.getAttribute('data-filter-name') || '')
    .trim()
    .toLowerCase()
  return new Set(raw ? raw.split(/\s+/) : [])
}

function readTarget(element) {
  return (element.getAttribute('data-filter-target') || '').trim().toLowerCase()
}

/**
 * @param {Element} button
 * @param {HTMLElement} group
 * @returns {HTMLElement}
 */
function readTargetScope(button, group) {
  const scope = button.closest(TARGET_MATCH_SELECTOR)
  return scope instanceof HTMLElement ? scope : group
}

/**
 * @param {HTMLElement} scope
 * @returns {string}
 */
function readTargetMatch(scope) {
  return (scope.getAttribute(TARGET_MATCH_ATTRIBUTE) || 'multi').toLowerCase()
}

/**
 * @param {Set<string>} activeTags
 * @param {string} target
 * @param {HTMLElement} scope
 * @returns {boolean}
 */
function selectOnly(activeTags, target, scope) {
  if (activeTags.has(target)) return false

  for (const button of scope.querySelectorAll(TARGET_SELECTOR)) {
    activeTags.delete(readTarget(button))
  }
  activeTags.add(target)
  return true
}

function setState(element, isOn, ariaName) {
  const next = isOn ? 'active' : 'not-active'
  if (element.getAttribute('data-filter-status') !== next) {
    element.setAttribute('data-filter-status', next)
  }
  element.setAttribute(
    ariaName,
    ariaName === 'aria-hidden' ? String(!isOn) : String(isOn)
  )
}

/**
 * @param {HTMLElement} group
 * @param {number} shown
 * @returns {void}
 */
function paintCount(group, shown) {
  const counter = group.querySelector(COUNT_SELECTOR)
  if (!counter) return

  const template = counter.getAttribute(COUNT_TEMPLATE_ATTRIBUTE)
  if (!template) return

  counter.textContent = template.replace(COUNT_SHOWN_TOKEN, String(shown))
}

function matchesTokens(tokens, selected) {
  return selected.some((tag) => tokens.has(tag))
}

function initGroup(group) {
  const buttons = [...group.querySelectorAll(TARGET_SELECTOR)]
  if (buttons.length === 0) return

  const tokensByItem = new WeakMap()
  const timers = new WeakMap()
  const activeTags = new Set()

  const readItems = () => [...group.querySelectorAll(NAME_SELECTOR)]

  const tokensOf = (item) => {
    const cached = tokensByItem.get(item)
    if (cached) return cached
    const tokens = readTokens(item)
    tokensByItem.set(item, tokens)
    return tokens
  }

  const itemMatches = (item) => {
    if (activeTags.size === 0) return true
    return matchesTokens(tokensOf(item), [...activeTags])
  }

  const paintItems = () => {
    let shown = 0

    for (const item of readItems()) {
      clearTimeout(timers.get(item))
      const next = itemMatches(item)
      if (next) shown += 1
      if (item.getAttribute('data-filter-status') === 'active') {
        item.setAttribute('data-filter-status', 'transition-out')
      }
      timers.set(
        item,
        setTimeout(() => setState(item, next, 'aria-hidden'), TRANSITION_MS)
      )
    }

    paintCount(group, shown)
  }

  const paintButtons = () => {
    for (const button of buttons) {
      const target = readTarget(button)
      let isOn = activeTags.has(target)
      if (target === 'all') isOn = activeTags.size === 0
      if (target === 'reset') isOn = activeTags.size > 0
      setState(button, isOn, 'aria-pressed')
    }
  }

  const apply = (target, scope) => {
    if (RESET_TARGETS.has(target)) {
      if (activeTags.size === 0) return false
      activeTags.clear()
      return true
    }
    if (readTargetMatch(scope) === 'single') {
      return selectOnly(activeTags, target, scope)
    }
    if (activeTags.has(target)) return activeTags.delete(target)
    activeTags.add(target)
    return true
  }

  const toggle = (target, scope) => {
    if (!apply(target, scope)) return

    paintItems()
    paintButtons()
  }

  group.addEventListener('click', (event) => {
    const button = event.target.closest(TARGET_SELECTOR)
    if (!(button && group.contains(button))) return
    toggle(readTarget(button), readTargetScope(button, group))
  })

  paintButtons()
}

for (const group of document.querySelectorAll(GROUP_SELECTOR)) {
  initGroup(group)
}
